import type { Task, TaskFilter } from '../api';

/**
 * Whether a surface's subject *is* blocked work, which is what un-caps the pill below.
 *
 * One definition rather than `filter.blocked === true` at the list and again at the board. The two
 * views share a filter by design, so two spellings of "is this screen about blockers" is two
 * places for them to answer differently — which is the whole family of defect `BlockedByPill`
 * exists to close, in miniature.
 */
export const blockersAreTheContent = (filter: Pick<TaskFilter, 'blocked'>): boolean =>
  filter.blocked === true;

/**
 * Who a blocked task is waiting on — not merely that it is stuck.
 *
 * Shared rather than redrawn per surface, for the reason `SubtaskPill` gives about itself: the last
 * defects the owner reported were each one surface drifting from the rest. This one had already
 * drifted when it was lifted here. The list drew the pill; the board card drew
 * `<StatusBadge status="blocked" compact />` and no text at all, so the view that looks built for
 * triage told you strictly less than the one beside it — a red ⊘ with no name does not say who to
 * chase.
 *
 * **`full` is the whole of the fix, and it is deliberately not the default.** Capped, the pill
 * printed `blockedBy[0].title` ellipsised at 16rem plus a bare ` +N`: measured at 1280 a single
 * blocker needed 369px of a 254px box, so ten blocked rows with four distinct blockers between
 * them rendered three *"Waiting on finance to c…"* and two *"legal redline…"*, with the full list
 * reachable only by hovering one row at a time with a mouse.
 *
 * The cap is still right where it came from. On a 61-row list that is not *about* blocked work, one
 * wrapping pill per row is the screen the cap exists to prevent — so the caller says whether its
 * screen is about blockers, and only there do they become the content. `blockedByNamed.e2e.spec.ts`
 * asserts both halves, because a fix that just deleted `max-width` looks identical on the screen
 * this was reported from.
 */
export function BlockedByPill({
  task,
  full = false,
}: {
  task: Pick<Task, 'isBlocked' | 'blockedBy'>;
  /**
   * List every blocker, wrapped, with no cap.
   *
   * For a surface whose subject is blocked work — the list filtered to `blocked`, and the board
   * under that same filter, the two views sharing one filter by design.
   */
  full?: boolean;
}) {
  /*
    Both halves, not just the flag. `isBlocked` is derived from open predecessors and `blockedBy`
    is the list behind it, and a pill drawn from the flag alone would have had to render "blocked
    by: undefined" for the window where one is true and the other empty.
  */
  if (!task.isBlocked || task.blockedBy.length === 0) return null;

  const titles = task.blockedBy.map((b) => b.title);
  return (
    <span
      className={full ? 'pill blocked-by is-full' : 'pill blocked-by'}
      data-testid="blocked-by"
      /*
        Kept on both forms. It is no longer the only way to read the names, which was the defect,
        but it is still the thing a pointer offers — and on the capped form it is the only way, so
        removing it with the cap in place would have traded one surface's problem for another's.
      */
      title={`Blocked by ${titles.join(', ')}`}
    >
      blocked by: {full ? titles.join(', ') : titles[0]}
      {!full && titles.length > 1 ? ` +${titles.length - 1}` : ''}
    </span>
  );
}
